import { Link } from '@ipfabric/ui-components/Link/Link';
import {
  type ReportSeverity,
  type TableColumnFormatText,
  getValueFromKeyOfType,
} from '@ipfabric/utils';
import type { RenderedTableCell } from 'components/Table/cells/RenderedTableCell';
import { maybeWrapNonEmptyWithLink } from 'components/Table/cells/helpers/maybeWrapWithLink';
import {
  ARRAY_CELL_MAX_ITEMS,
  maybeWrapWithMore,
} from 'components/Table/cells/helpers/trimLargeArray';
import type { TableCellContext } from 'components/Table/model/TableCellContext';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import type { ReactNode } from 'react';
import { isObject } from 'utils/object/isObject';
import { unsafeWrapWithReactKeyByIndex } from 'utils/react/unsafeWrapWithReactKeyByIndex';
import type { RenderCellContentArguments } from '../model/TableCellContentRenderFunction';

const formatMultiline = (
  content: string[],
  context: TableCellContext,
  index: number,
  severity: ReportSeverity | null,
): ReactNode =>
  context.detailView ? (
    content.map((value, i) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: no better key
      <span className='pb-1.5' key={i}>
        {value}
        {i < content.length - 1 && <br />}
      </span>
    ))
  ) : (
    <>
      {content[0]}{' '}
      {content.length > 1 ? (
        <Link
          accent={severity === null ? 'positive' : 'contrast'}
          renderAs='button'
          onClick={() => {
            context.onShowDetailClick?.(index);
          }}
        >
          (+
          {content.length - 1})
        </Link>
      ) : null}
    </>
  );

/**
 * Mitigate app crash when object appears instead of text
 *
 * Some autogenerated table yaml definitions were auto-generated with type:text
 * for all kinds of columns, which caused hard crash on frontend when trying to render
 * it as a React node. This way we mitigate it to make the table at least displayable
 * in such case.
 *
 * We usually assume correct data and definitions from API, but I decided to handle
 * this case as it is a common one with potentially big impact.
 */
const safeWrapObject = (content: string | null) =>
  isObject(content) ? (
    <span className='italic text-red-900'>(not displayable object)</span>
  ) : (
    content
  );

export const renderTextCell = <TRow,>(
  format: TableColumnFormatText<TRow>,
  { row, context, index, severity }: RenderCellContentArguments<TRow>,
): RenderedTableCell => {
  const content = getValueFromKeyOfType(row, format.field);

  if (format.multiline && Array.isArray(content)) {
    return {
      content: formatMultiline(content, context, index, severity),
    };
  }

  if (Array.isArray(content)) {
    return {
      content: pipe(
        content,
        (a) => a.slice(0, ARRAY_CELL_MAX_ITEMS),
        array.map((c) =>
          isObject(c)
            ? safeWrapObject(c)
            : // Links cannot be generated for array objects as their string value is unknown.
              maybeWrapNonEmptyWithLink(
                row,
                severity !== null,
                format.createLinkProps,
                !context.disableLinks,
              )(c, c),
        ),
        (a) => a.map(unsafeWrapWithReactKeyByIndex),
        array.reduce<ReactNode, ReactNode | null>(null, (prev, current) =>
          prev === null ? [current] : [prev, ', ', current],
        ),
        maybeWrapWithMore(content),
      ),
    };
  }

  return {
    content: pipe(
      content,
      safeWrapObject,
      maybeWrapNonEmptyWithLink(
        row,
        severity !== null,
        format.createLinkProps,
        !context.disableLinks,
      ),
    ),
  };
};
